<script setup>
    import { defineExpose, defineEmits, inject, ref, computed, watch, getCurrentInstance } from 'vue';
    import { useCompanyManagementListStore } from '@/stores/companyManagement/companyManagementListStore';
    import { CompanyManagementDetailRequest } from '@/models/companyManagement/companyManagementModel';
    import { OrganizationInfoRequest } from '@/models/management/managementModel';
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 管理者の管理ストア
    const companyManagementListStore = useCompanyManagementListStore();
    // 管理者詳細リクエスト項目
    const companyManagementDetailRequest = ref({});
    // 組織詳細リクエスト項目
    const organizationInfoRequest = ref({});
    // 選択対象組織リスト
    const selectMngHealthStaffOrgsList = ref([]);
    // 対象組織項目
    const organizationTarget = ref([]);
    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // 対象組織の各チェックボックス
    const selectedCheckbox = ref([]);
    // 日付情報
    const selectedDate1 = ref('');
    const selectedDate2 = ref('');
    // 遷移元判別フラグ
    const userSelectFlg = ref(false);

    const allOrganizationSelected = ref(false);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    // 入力欄活性コントロール
    // ユーザーID
    const disabledUserPk = computed(() => {
        return !Utils.isNullEmpty(companyManagementDetailRequest.value.UserPk);
    });
    const touDataFlag = computed(() => {
        return companyManagementDetailRequest.value.TouDataFlag;
    });
    // パスワード種別ラジオボタン1
    const disabledIniPwdType1 = computed(() => {
        let flg = false;
        // 編集モードで本モーダルを開いた場合、一度でもログイン済みの場合は、初期パスワードの設定モードの変更は不可能。
        if (!userSelectFlg.value && !Utils.isNullEmpty(companyManagementDetailRequest.value.UserPk) && !Utils.isNullEmpty(companyManagementDetailRequest.value.LastLoginDt)) {
            flg = true;
        }

        // 認証方法が「タフネス連携」のユーザーは、初期パスワードの選択肢のラジオボタンを非活性にする。
        if (companyManagementDetailRequest.value.AuthenticateMethod === '2') {
            flg = true;
        }
        return flg;
    });
    // パスワード種別ラジオボタン2
    const disabledIniPwdType2 = computed(() => {
        let flg = false;
        // 新規登録モードで本モーダルを開いた場合、一度でもログイン済みの場合は、初期パスワードの設定モードの変更は不可能。
        if (!userSelectFlg.value && (Utils.isNullEmpty(companyManagementDetailRequest.value.UserPk) || !Utils.isNullEmpty(companyManagementDetailRequest.value.LastLoginDt))) {
            flg = true;
        }

        // 認証方法が「タフネス連携」のユーザーは、初期パスワードの選択肢のラジオボタンを非活性にする。
        if (companyManagementDetailRequest.value.AuthenticateMethod === '2') {
            flg = true;
        }
        return flg;
    });

    /**
     * 初期処理 親画面から利用可能関数
     */
    async function setInfo(UserPk) {
        // 初期化する。
        reset();

        if (!Utils.isNullEmpty(UserPk)) {
            // 編集モードの場合。
            companyManagementDetailRequest.value.UserPk = UserPk;

            //await getOrgList();
            await getOrgSelectList();

            // 管理者詳細取得
            await getDetail(companyManagementDetailRequest.value.UserPk);

            // 日付フォーマット
            await setData();

            // 初期パスワード種別調整
            if (companyManagementDetailRequest.value.IniPwdType === '2') {
                companyManagementDetailRequest.value.IniPwdType = '1';
            }

            if (Utils.isNullEmpty(companyManagementDetailRequest.value.UserPk)) {
                companyManagementDetailRequest.value.IniPwdType = '1';
            } else if (!Utils.isNullEmpty(companyManagementDetailRequest.value.UserPk) && !Utils.isNullEmpty(companyManagementDetailRequest.value.LastLoginDt)) {
                companyManagementDetailRequest.value.IniPwdType = '0';
            }

            // 認証方法が「タフネス連携」のユーザーは、初期パスワードの変更は不可なので、初期パスワードラジオボタンの座標を「設定(変更)しない」に設定する。
            if (companyManagementDetailRequest.value.AuthenticateMethod === '2') {
                // ラジオボタンの座標を「設定(変更)しない」に設定する。
                companyManagementDetailRequest.value.IniPwdType = '0';
            }
            //await allSelected();
            //await checkedOrgCheckBox();
        } else {
            // 新規登録の場合。
            //await getOrgList();
            await getOrgSelectList();
            //await allSelected();

            // 初期値を設定する。
            // 利用者の登録の初期値は「なし」
            companyManagementDetailRequest.value.CliFlag = false;
            // 登録契機の初期値は「管理者登録」
            companyManagementDetailRequest.value.RegistMethod = '1';
            // 認証方法の初期値は「カウンセリングシステム」
            companyManagementDetailRequest.value.AuthenticateMethod = '1';
        }

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalAdministratorListAdministratorDetail .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }
    defineExpose({
        setInfo,
        selectModal,
    });

    /**
     * 日付設定処理
     */
    async function setData() {
        selectedDate1.value = convertDate2(companyManagementDetailRequest.value.UseStartDate);
        selectedDate2.value = convertDate2(companyManagementDetailRequest.value.UseEndDate);
    }

    /**
     * 日付設定処理
     */
    // ----------------------------
    // watch
    // ----------------------------
    watch(selectedDate1, (newValue) => {
        if (newValue) {
            companyManagementDetailRequest.value.UseStartDate = convertDate(newValue);
        } else {
            companyManagementDetailRequest.value.UseStartDate = '';
        }
    });
    watch(selectedDate2, (newValue) => {
        if (newValue) {
            companyManagementDetailRequest.value.UseEndDate = convertDate(newValue);
        } else {
            companyManagementDetailRequest.value.UseEndDate = '';
        }
    });

    // 日付アレンジ用変換メソッド
    function convertDate(input) {
        // 取得したデータがnullの場合は空を返却する。
        if (Utils.isNullEmpty(input)) {
            return '';
        }
        const [year, month, day] = input.split('-');
        return `${year}/${month}/${day}`;
    }
    function convertDate2(input) {
        // 取得したデータがnullの場合は空を返却する。
        if (Utils.isNullEmpty(input)) {
            return '';
        }
        const [year, month, day] = input.split('/');
        return `${year}-${month}-${day}`;
    }

    /**
     * 「全組織のレポートを参照する」チェックボックスの変更をトリガーに監視する。
     */
    async function allSelected() {
        // 全組織のレポートを参照するボタンの状態に応じて、対象組織の一覧のチェックボックスの活性・非活性を制御する。
        allOrganizationSelected.value = companyManagementDetailRequest.value.MngHealthStaffOrgAll;
    }

    /**
     * 管理者取得処理
     */
    async function getDetail(UserPk) {
        // 管理者取得APIのクエリパラメーターを生成する。
        const queryParam = {
            UserPk: UserPk,
        };

        // 管理者取得APIをコールする。
        const response = await companyManagementListStore.getMngDtlAction(API_URL, queryParam);

        if (response.success) {
            // 管理者の詳細情報を格納して、画面に表示する。
            companyManagementDetailRequest.value = Object.assign(companyManagementDetailRequest.value, companyManagementListStore.mngDetailInfo);

            // 産業保健スタッフ全組織選択がnullでは無い場合は、産業保健スタッフ全組織選択フィールドの状態を画面に反映する。
            if (!Utils.isNullEmpty(companyManagementDetailRequest.value.MngHealthStaffOrgAll)) {
                // 全組織のレポートを参照するボタンの状態に応じて、対象組織の一覧のチェックボックスの活性・非活性を制御する。
                allOrganizationSelected.value = companyManagementDetailRequest.value.MngHealthStaffOrgAll;
            }

            // APIから取得した、選択している対象組織を復元し、画面のチェックボックスを制御する。
            if (!Utils.isNullEmptyArray(companyManagementDetailRequest.value.MngHealthStaffOrgs)) {
                // 対象組織格納用配列に格納する。
                let selectedOrganizationCheckbox = [];
                for (const mngHealthStaffOrgs of companyManagementDetailRequest.value.MngHealthStaffOrgs) {
                    selectedOrganizationCheckbox.push(mngHealthStaffOrgs.OrganizationPk);
                }
                // 画面に表示する。
                selectedCheckbox.value = selectedOrganizationCheckbox;
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 組織選択処理
     */
    async function getOrgSelectList() {
        // 組織選択取得APIをコールする。
        // 第1階層のみ取得したいので、クエリパラメーターは設定しない。
        const response = await companyManagementListStore.getOrgSelAction(API_URL, null);

        // 登録されている全組織の一覧を、権限設定ブロックの対象組織のチェックボックスエリアに生成するための下準備。
        if (response.success) {
            organizationTarget.value = [];
            for (const organization of response.responseBody) {
                const orgObject = {
                    OrganizationPk: organization.OrganizationPk,
                    OrganizationId: organization.OrganizationId,
                    OrganizationNmP: organization.OrganizationNm,
                };
                organizationTarget.value.push(orgObject);
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 管理者登録or更新処理
     */
    async function createDetail() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }

        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // 対象組織格納用配列に格納する。
        let selectedOrganizationCheckbox = null;

        // 選択した対象組織のチェックボックスをコピーする。
        if (!Utils.isNullEmptyArray(selectedCheckbox.value)) {
            selectedOrganizationCheckbox = JSON.parse(JSON.stringify(selectedCheckbox.value));
        }

        // 全組織のレポートを参照するチェックボックスが有効の場合。
        if (companyManagementDetailRequest.value.MngHealthStaffOrgAll) {
            let array = [];
            // 全ての対象組織の組織PKを抽出する。
            for (const organization of organizationTarget.value) {
                array.push(organization.OrganizationPk);
            }
            // 対象組織格納用配列に格納する。
            selectedOrganizationCheckbox = array;
        }

        // 本モーダルの登録モードに応じて実行するAPIや、設定するリクエストボディ構造を制御する。
        if (userSelectFlg.value) {
            // 利用者から新規登録する場合。

            // 管理者登録APIのリクエストボディを生成する。
            const requestBody = {
                UserPk: companyManagementDetailRequest.value.UserPk,
                UserId: companyManagementDetailRequest.value.UserId,
                MailAddress: companyManagementDetailRequest.value.MailAddress,
                IniPwdType: companyManagementDetailRequest.value.IniPwdType,
                NameKjLs: companyManagementDetailRequest.value.NameKjLs,
                NameKjFs: companyManagementDetailRequest.value.NameKjFs,
                NameKnLs: Utils.changeNullValueToEmpty(companyManagementDetailRequest.value.NameKnLs),
                NameKnFs: Utils.changeNullValueToEmpty(companyManagementDetailRequest.value.NameKnFs),
                SuspendFlag: companyManagementDetailRequest.value.SuspendFlag,
                UseStartDate: Utils.changeNullValueToEmpty(companyManagementDetailRequest.value.UseStartDate),
                UseEndDate: Utils.changeNullValueToEmpty(companyManagementDetailRequest.value.UseEndDate),
                MngSystemAdminFlag: Utils.isNull(companyManagementDetailRequest.value.MngSystemAdminFlag) ? false : companyManagementDetailRequest.value.MngSystemAdminFlag,
                MngServiceAdminFlag: Utils.isNull(companyManagementDetailRequest.value.MngServiceAdminFlag) ? false : companyManagementDetailRequest.value.MngServiceAdminFlag,
                MngHealthStaffFlag: Utils.isNull(companyManagementDetailRequest.value.MngHealthStaffFlag) ? false : companyManagementDetailRequest.value.MngHealthStaffFlag,
                MngHealthStaffOrgAll: Utils.isNull(companyManagementDetailRequest.value.MngHealthStaffOrgAll) ? false : companyManagementDetailRequest.value.MngHealthStaffOrgAll,
                MngHealthStaffOrgPks: selectedOrganizationCheckbox,
                MngIncidentReportFlag: Utils.isNull(companyManagementDetailRequest.value.MngIncidentReportFlag) ? false : companyManagementDetailRequest.value.MngIncidentReportFlag,
                MngCrisisReportFlag: Utils.isNull(companyManagementDetailRequest.value.MngCrisisReportFlag) ? false : companyManagementDetailRequest.value.MngCrisisReportFlag,
            };
            // 管理者登録APIをコールする。
            const response = await companyManagementListStore.postMngDtlAction(API_URL, requestBody);
            if (response.success) {
                closeAndUpdate();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        } else {
            if (companyManagementDetailRequest.value.UserPk === '') {
                // 新規登録の場合。

                // 管理者登録APIのリクエストボディを生成する。
                const requestBody = {
                    UserId: companyManagementDetailRequest.value.UserId,
                    MailAddress: companyManagementDetailRequest.value.MailAddress,
                    IniPwdType: companyManagementDetailRequest.value.IniPwdType,
                    NameKjLs: companyManagementDetailRequest.value.NameKjLs,
                    NameKjFs: companyManagementDetailRequest.value.NameKjFs,
                    NameKnLs: companyManagementDetailRequest.value.NameKnLs,
                    NameKnFs: companyManagementDetailRequest.value.NameKnFs,
                    SuspendFlag: companyManagementDetailRequest.value.SuspendFlag,
                    UseStartDate: companyManagementDetailRequest.value.UseStartDate,
                    UseEndDate: companyManagementDetailRequest.value.UseEndDate,
                    MngSystemAdminFlag: companyManagementDetailRequest.value.MngSystemAdminFlag,
                    MngServiceAdminFlag: companyManagementDetailRequest.value.MngServiceAdminFlag,
                    MngHealthStaffFlag: companyManagementDetailRequest.value.MngHealthStaffFlag,
                    MngHealthStaffOrgAll: companyManagementDetailRequest.value.MngHealthStaffOrgAll,
                    MngHealthStaffOrgPks: selectedOrganizationCheckbox,
                    MngIncidentReportFlag: companyManagementDetailRequest.value.MngIncidentReportFlag,
                    MngCrisisReportFlag: companyManagementDetailRequest.value.MngCrisisReportFlag,
                };
                // 管理者登録APIをコールする。
                const response = await companyManagementListStore.postMngDtlAction(API_URL, requestBody);
                if (response.success) {
                    closeAndUpdate();
                } else {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return;
                }
            } else {
                // 編集モードの場合。

                // 管理者登録APIのリクエストボディを生成する。
                const requestBody = {
                    UserPk: companyManagementDetailRequest.value.UserPk,
                    MailAddress: companyManagementDetailRequest.value.MailAddress,
                    IniPwdType: companyManagementDetailRequest.value.IniPwdType,
                    NameKjLs: companyManagementDetailRequest.value.NameKjLs,
                    NameKjFs: companyManagementDetailRequest.value.NameKjFs,
                    NameKnLs: companyManagementDetailRequest.value.NameKnLs,
                    NameKnFs: companyManagementDetailRequest.value.NameKnFs,
                    SuspendFlag: companyManagementDetailRequest.value.SuspendFlag,
                    UseStartDate: companyManagementDetailRequest.value.UseStartDate,
                    UseEndDate: companyManagementDetailRequest.value.UseEndDate,
                    MngSystemAdminFlag: companyManagementDetailRequest.value.MngSystemAdminFlag,
                    MngServiceAdminFlag: companyManagementDetailRequest.value.MngServiceAdminFlag,
                    MngHealthStaffFlag: companyManagementDetailRequest.value.MngHealthStaffFlag,
                    MngHealthStaffOrgAll: companyManagementDetailRequest.value.MngHealthStaffOrgAll,
                    MngHealthStaffOrgPks: selectedOrganizationCheckbox,
                    MngIncidentReportFlag: companyManagementDetailRequest.value.MngIncidentReportFlag,
                    MngCrisisReportFlag: companyManagementDetailRequest.value.MngCrisisReportFlag,
                };
                // 管理者登録APIをコールする。
                const response = await companyManagementListStore.putMngDtlAction(API_URL, requestBody);
                if (response.success) {
                    closeAndUpdate();
                } else {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return;
                }
            }
        }
    }

    /**
     * 変数初期化処理
     */
    function reset() {
        // 管理者詳細リクエスト項目の初期化
        companyManagementDetailRequest.value = Object.assign({}, new CompanyManagementDetailRequest());

        // 組織詳細リクエスト項目の初期化
        organizationInfoRequest.value = Object.assign({}, new OrganizationInfoRequest());

        // 対象組織項目の初期化
        organizationTarget.value = [];

        // 選択対象組織リストの初期化
        selectMngHealthStaffOrgsList.value = [];

        // 遷移元判別フラグ
        userSelectFlg.value = false;

        // 利用開始日
        selectedDate1.value = '';

        // 利用終了日
        selectedDate2.value = '';

        // 対象組織の一覧から、チェックボックスで選択した組織PKの配列
        selectedCheckbox.value = [];
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';
        // ID
        if (Utils.isNullEmpty(companyManagementDetailRequest.value.UserId)) {
            message += i18n.global.t('CompanyManagementDetail.message.message1') + '\n';
        } else {
            // 文字数
            if (Utils.checkLengthOfUserId(companyManagementDetailRequest.value.UserId)) {
                message += i18n.global.t('ManagementDetail.message.message13') + '\n';
            }
            // 形式
            if (!Utils.isMatchPatternLowerAlphabetNumber(companyManagementDetailRequest.value.UserId)) {
                message += i18n.global.t('ManagementDetail.message.message14') + '\n';
            }
        }
        // 氏名(漢字)(性)
        if (Utils.isNullEmpty(companyManagementDetailRequest.value.NameKjLs)) {
            message += i18n.global.t('CompanyManagementDetail.message.message2') + '\n';
        } else {
            // 文字数
            if (Utils.checkLengthOfShortString(companyManagementDetailRequest.value.NameKjLs)) {
                message += i18n.global.t('ManagementDetail.message.message16') + '\n';
            }
        }
        // 氏名(漢字)(名)
        if (Utils.isNullEmpty(companyManagementDetailRequest.value.NameKjFs)) {
            message += i18n.global.t('CompanyManagementDetail.message.message3') + '\n';
        } else {
            // 文字数
            if (Utils.checkLengthOfShortString(companyManagementDetailRequest.value.NameKjFs)) {
                message += i18n.global.t('ManagementDetail.message.message17') + '\n';
            }
        }
        // 氏名(カナ)(性)
        if (!Utils.isNullEmpty(companyManagementDetailRequest.value.NameKnLs)) {
            // 文字数
            if (Utils.checkLengthOfString(companyManagementDetailRequest.value.NameKnLs)) {
                message += i18n.global.t('ManagementDetail.message.message18') + '\n';
            }
            // 形式
            if (!Utils.isMatchPatternKana(companyManagementDetailRequest.value.NameKnLs)) {
                message += i18n.global.t('ManagementDetail.message.message7') + '\n';
            }
        }
        // 氏名(カナ)(名)
        if (!Utils.isNullEmpty(companyManagementDetailRequest.value.NameKnFs)) {
            // 文字数
            if (Utils.checkLengthOfString(companyManagementDetailRequest.value.NameKnFs)) {
                message += i18n.global.t('ManagementDetail.message.message19') + '\n';
            }
            // 形式
            if (!Utils.isMatchPatternKana(companyManagementDetailRequest.value.NameKnFs)) {
                message += i18n.global.t('ManagementDetail.message.message8') + '\n';
            }
        }
        // メールアドレス
        if (Utils.isNullEmpty(companyManagementDetailRequest.value.MailAddress)) {
            message += i18n.global.t('CompanyManagementDetail.message.message10') + '\n';
        } else {
            // 文字数
            if (Utils.checkLengthOfMailAddress(companyManagementDetailRequest.value.MailAddress)) {
                message += i18n.global.t('CompanyManagementDetail.message.message8') + '\n';
            }
            // 形式
            if (!Utils.isMatchPatternMailAddress(companyManagementDetailRequest.value.MailAddress)) {
                message += i18n.global.t('CompanyManagementDetail.message.message9') + '\n';
            }
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    /**
     * ユーザー選択モーダルから管理者詳細モーダルを開くイベントが実行された場合の処理。
     * @param {String} userPk
     * @returns なし
     */
    async function selectModal(UserPk) {
        // 初期化する。
        reset();

        await getDetail(UserPk);
        //await getOrgList();
        await getOrgSelectList();

        // ユーザー引き継ぎ時の初期値を設定する。
        userSelectFlg.value = true;

        // 新規登録モードなので、引き継いだユーザーPKは不要なので、リクエストボディ構造上不要にする。
        companyManagementDetailRequest.value.UserPk = UserPk;
    }

    /**
     * 親画面で実装必須関数
     */
    const emit = defineEmits(['detailModal:cancel', 'detailModal:closeAndUpdate']);
    function cancel() {
        // 親画面のキャンセル処理を呼び出す
        emit('detailModal:cancel');
    }

    function closeAndUpdate() {
        // 親画面の、モーダルを閉じて、一覧画面の再描画を行う処理を呼び出す。
        emit('detailModal:closeAndUpdate');
    }
</script>
<template>
    <article id="modalAdministratorListAdministratorDetail" class="modal" :style="{ display: 'block' }">
        <h3 class="textCenter">{{ $t('CompanyManagementDetail.parts.pageTitle1') }}</h3>
        <section class="modalContents" style="height: calc(100% - 145px)">
            <section class="form dtWidth245">
                <!-- 基本情報 -->
                <h4>{{ $t('CompanyManagementDetail.parts.pageTitle2') }}</h4>
                <!-- ユーザーID -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl2') }}</label>
                            </li>
                            <li class="must">
                                <span>{{ $t('CompanyManagementDetail.parts.dl1') }}</span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" v-model="companyManagementDetailRequest.UserId" :disabled="disabledUserPk" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- メールアドレス -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl3') }}</label>
                            </li>
                            <li class="must">
                                <span>{{ $t('CompanyManagementDetail.parts.dl1') }}</span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" v-model="companyManagementDetailRequest.MailAddress" :disabled="touDataFlag" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 初期パスワード -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl4') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul>
                            <li style="margin-bottom: 5px">
                                <!--設定(変更)する(初回ログイン以降の変更は不可)-->
                                <input type="radio" class="radio" id="radio-1" name="radio-group-1" value="1" v-model="companyManagementDetailRequest.IniPwdType" :disabled="disabledIniPwdType1" />
                                <label class="radio label" style="width: 325px" for="radio-1">{{ $t('CompanyManagementDetail.parts.pageInfo1') }}</label>
                                <div style="padding-left: 28px">
                                    <!--ランダムなパスワードを生成してユーザーにメール送信-->
                                    <p>{{ $t('CompanyManagementDetail.parts.pageInfo2') }}</p>
                                </div>
                            </li>
                            <li style="margin-bottom: 10px">
                                <!--設定(変更)しない-->
                                <input type="radio" class="radio" id="radio-2" name="radio-group-1" value="0" v-model="companyManagementDetailRequest.IniPwdType" :disabled="disabledIniPwdType2" />
                                <label class="radio label" style="width: 134px" for="radio-2">{{ $t('CompanyManagementDetail.parts.dl5') }}</label>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 氏名(漢字) -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl6') }}</label>
                            </li>
                            <li class="must">
                                <span>{{ $t('CompanyManagementDetail.parts.dl1') }}</span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" class="smallField" v-model="companyManagementDetailRequest.NameKjLs" :disabled="touDataFlag" />
                            </li>
                            <li>
                                <input type="text" class="smallField" v-model="companyManagementDetailRequest.NameKjFs" :disabled="touDataFlag" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 氏名(カナ) -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl7') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" class="smallField" v-model="companyManagementDetailRequest.NameKnLs" :disabled="touDataFlag" />
                            </li>
                            <li>
                                <input type="text" class="smallField" v-model="companyManagementDetailRequest.NameKnFs" :disabled="touDataFlag" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 利用者の登録 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl8') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li v-if="companyManagementDetailRequest.CliFlag">
                                <span>{{ $t('CompanyManagementDetail.parts.dl9') }}</span>
                            </li>
                            <li v-else>
                                <span>{{ $t('CompanyManagementDetail.parts.dl10') }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 登録契機 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl11') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li v-if="companyManagementDetailRequest.RegistMethod === '1'">
                                <span>{{ $t('CompanyManagementDetail.parts.dl12') }}</span>
                            </li>
                            <li v-else-if="companyManagementDetailRequest.RegistMethod === '2'">
                                <span>{{ $t('CompanyManagementDetail.parts.dl13') }}</span>
                            </li>
                            <li v-else-if="companyManagementDetailRequest.RegistMethod === '3'">
                                <span>{{ $t('CompanyManagementDetail.parts.dl14') }}</span>
                            </li>
                            <li v-else-if="companyManagementDetailRequest.RegistMethod === '4'">
                                <span>{{ $t('CompanyManagementDetail.parts.dl15') }}</span>
                            </li>
                            <li v-else-if="companyManagementDetailRequest.RegistMethod === '5'">
                                <span>{{ $t('CompanyManagementDetail.parts.dl16') }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 認証方法 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl17') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li v-if="companyManagementDetailRequest.AuthenticateMethod === '1'">
                                <!--カウンセリングシステム-->
                                <span>{{ $t('CompanyManagementDetail.parts.dl18') }}</span>
                            </li>
                            <li v-else-if="companyManagementDetailRequest.AuthenticateMethod === '2'">
                                <!--タフネス-->
                                <span>{{ $t('CompanyManagementDetail.parts.dl19') }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 利用停止 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl21') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li style="width: 21px">
                                <input type="checkbox" class="checkbox" id="checkbox-1" v-model="companyManagementDetailRequest.SuspendFlag" :value="true" />
                                <label class="checkbox" for="checkbox-1" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 利用開始日-利用終了日 -->
                <dl class="marginBottom15">
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl22') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li style="margin-right: 5px; width: 210px">
                                <label class="calendar" style="width: 210px">
                                    <input type="date" :class="[selectedDate1 !== '' ? 'fillterText' : 'fillterText blank']" placeholder="日付を選択してください" v-model="selectedDate1" />
                                </label>
                            </li>
                            <li style="width: auto; margin-right: 5px">
                                <span>-</span>
                            </li>
                            <li>
                                <label class="calendar" style="width: 210px">
                                    <input type="date" :class="[selectedDate2 !== '' ? 'fillterText' : 'fillterText blank']" placeholder="日付を選択してください" v-model="selectedDate2" />
                                </label>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 権限設定 -->
                <h4 style="margin-top: 40px">{{ $t('CompanyManagementDetail.parts.pageTitle3') }}</h4>
                <!-- システム管理者 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl23') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li style="width: 21px">
                                <input type="checkbox" class="checkbox" id="checkbox-2-1" v-model="companyManagementDetailRequest.MngSystemAdminFlag" :value="true" />
                                <label class="checkbox" for="checkbox-2-1" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- サービス管理者 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl24') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li style="width: 21px">
                                <input type="checkbox" class="checkbox" id="checkbox-2-2" v-model="companyManagementDetailRequest.MngServiceAdminFlag" :value="true" />
                                <label class="checkbox" for="checkbox-2-2" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 産業保健スタッフ -->
                <dl style="margin-bottom: unset">
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('CompanyManagementDetail.parts.dl25') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput" style="align-items: start">
                            <li style="margin-top: 5px; width: 70px">
                                <input type="checkbox" class="checkbox" id="checkbox-2-3" v-model="companyManagementDetailRequest.MngHealthStaffFlag" :value="true" />
                                <label class="checkbox" style="width: 21px" for="checkbox-2-3" />
                            </li>
                            <li style="margin-top: 5px">
                                <dl style="margin-bottom: 0px">
                                    <!--対象組織-->
                                    <dd style="width: 436px">
                                        <ul class="formInput" style="display: flex; align-items: center">
                                            <li style="width: 150px; margin-right: unset">
                                                <span>{{ $t('CompanyManagementDetail.parts.dl26') }}</span>
                                            </li>
                                            <li style="margin-left: auto">
                                                <!--全組織のレポートを参照する-->
                                                <input type="checkbox" class="checkbox" id="checkbox-3" v-model="companyManagementDetailRequest.MngHealthStaffOrgAll" @change="allSelected" />
                                                <label class="checkbox label" for="checkbox-3">{{ $t('CompanyManagementDetail.parts.dl27') }}</label>
                                            </li>
                                        </ul>
                                    </dd>
                                </dl>
                                <dl style="margin-top: 5px">
                                    <!--対象組織の一覧-->
                                    <dd>
                                        <ul class="formInput">
                                            <li>
                                                <div id="administratorOrgSetting">
                                                    <ul class="scroll">
                                                        <li v-for="(data, index) in organizationTarget" :key="index">
                                                            <input type="checkbox" :id="'checkbox-3-' + (index + 1)" class="checkbox" :value="data.OrganizationPk" v-model="selectedCheckbox" :disabled="allOrganizationSelected" />
                                                            <label class="checkbox label" :for="'checkbox-3-' + (index + 1)">{{ data.OrganizationNmP }}</label>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </li>
                                        </ul>
                                    </dd>
                                </dl>
                            </li>
                        </ul>
                    </dd>
                </dl>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <!--キャンセル-->
                    <button type="button" class="btnNormal closeModal" @click="cancel()">{{ $t('CompanyManagementDetail.parts.button1') }}</button>
                </li>
                <li>
                    <!--登録-->
                    <button type="button" class="btnPrimary closeModal" @click="createDetail()" :disabled="disabledStore.isDisabled">{{ $t('CompanyManagementDetail.parts.button2') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>
<style></style>
